/**
 * uiInteraction.ts – Generic DOM interaction helpers for AI agents
 *
 * Provides functions that let an AI agent interact with the host application's
 * UI through `data-*` attribute annotations on DOM elements:
 *
 *   data-group-key   – Groups / containers
 *   data-button-key  – Clickable buttons
 *   data-input-key   – Text inputs
 *   data-select-key  – Selection controls (radio groups, dropdowns)
 *   data-info-key    – Read-only labels / info elements
 *
 * These helpers are designed to be passed directly into the `api` object of
 * `createEvaluate()`, so the AI can call them from generated code.
 *
 * Security:
 *   - All user-facing strings are sanitized (HTML tags stripped, length limited)
 *   - UI interactions are rate-limited (default: 60 per minute)
 *   - The highlight class is automatically removed after a timeout
 */

// ── Utilities ─────────────────────────────────────────────────────────────────

/** Waits for the given number of milliseconds. */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Sanitization ──────────────────────────────────────────────────────────────

/**
 * Strip HTML tags and enforce a maximum length to prevent DOM-based XSS
 * when values are interpolated into selectors or innerHTML contexts.
 */
export function sanitizeString(value: string, maxLength: number = 1000): string {
  if (typeof value !== "string") return String(value);
  const clean = value.replace(/<[^>]*>/g, "");
  return clean.length > maxLength ? clean.substring(0, maxLength) : clean;
}

// ── Rate Limiting ─────────────────────────────────────────────────────────────

/**
 * Creates a simple sliding-window rate limiter.
 * Throws an Error when the limit is exceeded.
 */
export function createRateLimiter(
  name: string,
  maxCalls: number,
  windowMs: number = 60_000
): () => void {
  let count = 0;
  let windowStart = Date.now();
  return () => {
    const now = Date.now();
    if (now - windowStart > windowMs) {
      count = 0;
      windowStart = now;
    }
    count++;
    if (count > maxCalls) {
      throw new Error(
        `Rate limit for ${name} reached (max ${maxCalls} per ${windowMs / 1000}s). Please wait a moment.`
      );
    }
  };
}

// ── Configuration ─────────────────────────────────────────────────────────────

export interface UIInteractionOptions {
  /** CSS class added to highlighted elements (default: "ai-highlighted") */
  highlightClass?: string;
  /** Duration in ms the highlight stays visible (default: 10000) */
  highlightDurationMs?: number;
  /** Max UI interactions per rate-limit window (default: 60) */
  rateLimitMaxCalls?: number;
  /** Rate-limit window in ms (default: 60000) */
  rateLimitWindowMs?: number;
}

// ── Factory ───────────────────────────────────────────────────────────────────

export interface UIInteractionHelpers {
  /** Highlight a UI element by its data-* key value. Scrolls into view and adds a CSS class for a few seconds.
   * Automatically retries for up to 1500 ms; resolves silently if the element never appears (cosmetic only). */
  highlightUIElement: (name: string) => Promise<void>;
  /** Click a button identified by data-button-key.
   * Automatically retries for up to 1500 ms if the element is not yet in the DOM. */
  clickUIElement: (name: string) => Promise<void>;
  /** Set a radio / toggle value inside a data-select-key container.
   * Automatically retries for up to 1500 ms until both the container and the option appear in the DOM. */
  setUISelectValue: (selectName: string, optionValue: string) => Promise<void>;
  /** Set the value of a text input inside a data-input-key container (React-compatible).
   * Automatically retries for up to 1500 ms if the container is not yet in the DOM. */
  setUIInputValue: (inputName: string, value: string | number) => Promise<void>;
}

/**
 * Creates a set of UI interaction helper functions that can be spread
 * into the `api` object passed to `createEvaluate()`.
 *
 * @example
 * ```ts
 * import { createUIInteractionHelpers, createEvaluate, getCurrentUIState } from "btg-ai-agent";
 *
 * const uiHelpers = createUIInteractionHelpers();
 *
 * const evaluate = createEvaluate({
 *   api: {
 *     ...myDomainFunctions,
 *     ...uiHelpers,
 *     getCurrentUIState: () => getCurrentUIState(),
 *   },
 * });
 * ```
 */
export function createUIInteractionHelpers(
  options: UIInteractionOptions = {}
): UIInteractionHelpers {
  const {
    highlightClass = "ai-highlighted",
    highlightDurationMs = 10_000,
    rateLimitMaxCalls = 60,
    rateLimitWindowMs = 60_000,
  } = options;

  const rateLimit = createRateLimiter("UI interaction", rateLimitMaxCalls, rateLimitWindowMs);

  // ── Internal helpers ────────────────────────────────────────────────────


  /**
   * Polls `find()` every 100 ms for up to `timeoutMs` ms.
   * Resolves as soon as the element is found; rejects with `errorMessage` on timeout.
   */
  async function waitForElement<T extends Element>(
    find: () => T | null,
    errorMessage: string,
    timeoutMs = 1500
  ): Promise<T> {
    const interval = 100;
    let elapsed = 0;
    while (elapsed < timeoutMs) {
      const el = find();
      if (el) return el;
      await sleep(interval);
      elapsed += interval;
    }
    throw new Error(errorMessage);
  }

  // ── highlightUIElement ──────────────────────────────────────────────────

  async function highlightUIElement(name: string): Promise<void> {
    const safeName = sanitizeString(name, 200).replace(/"/g, '\\"');
    const selector = [
      "data-input-key",
      "data-button-key",
      "data-info-key",
      "data-select-key",
      "data-group-key",
      "data-value",
    ]
      .map((key) => `*[${key}="${safeName}"]`)
      .join(",");

    console.log('highlightUIElement("' + name + '")');

    // Wait for at least one matching element; silently skip if none appear (cosmetic only)
    try {
      await waitForElement(() => document.querySelector(selector) as HTMLElement | null, "");
    } catch {
      return;
    }

    const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
    elements.forEach((el) => {
      el.classList.add(highlightClass);
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
      el.focus();
    });
    sleep(highlightDurationMs).then(() =>
      elements.forEach((el) => el.classList.remove(highlightClass))
    );
  }

  // ── clickUIElement ──────────────────────────────────────────────────────

  async function clickUIElement(name: string): Promise<void> {
    rateLimit();
    const safeName = sanitizeString(name, 200);
    const el = await waitForElement(
      () => document.querySelector(`*[data-button-key="${safeName}"]`) as HTMLElement | null,
      `UI element "${name}" not found. Use getCurrentUIState() to discover valid keys.`
    );
    highlightUIElement(name);
    el.click();
  }

  // ── setUISelectValue ────────────────────────────────────────────────────

  async function setUISelectValue(selectName: string, optionValue: string): Promise<void> {
    rateLimit();
    const safeName = sanitizeString(selectName, 200);
    const safeValue = sanitizeString(optionValue, 200);

    // Wait until both the container AND the matching radio option exist in the DOM
    const radio = await waitForElement(
      () => {
        const container = document.querySelector(
          `*[data-select-key="${safeName}"]`
        ) as HTMLElement | null;
        if (!container) return null;
        return container.querySelector(
          `input[type="radio"][value="${safeValue}"], input[type="radio"][id="${safeValue}"]`
        ) as HTMLInputElement | null;
      },
      `Option "${optionValue}" in select "${selectName}" not found. Use getCurrentUIState() to discover valid options[].`
    );
    highlightUIElement(selectName);
    radio.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
    radio.click();
  }

  // ── setUIInputValue ─────────────────────────────────────────────────────

  async function setUIInputValue(inputName: string, value: string | number): Promise<void> {
    rateLimit();
    const safeName = sanitizeString(inputName, 200);
    const container = await waitForElement(
      () =>
        document.querySelector(`*[data-input-key="${safeName}"]`) as HTMLElement | null,
      `Input "${inputName}" not found. Use getCurrentUIState() to discover valid keys.`
    );
    highlightUIElement(inputName);

    const input = (
      container.tagName === "INPUT" ? container : container.querySelector("input")
    ) as HTMLInputElement | null;
    if (!input) {
      throw new Error(`No <input> element found inside "${inputName}".`);
    }

    const sanitizedValue = typeof value === "number" ? value : sanitizeString(String(value), 500);

    // React-compatible value setter via native descriptor + synthetic events
    const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
    if (nativeSetter) {
      nativeSetter.call(input, String(sanitizedValue));
    } else {
      input.value = String(sanitizedValue);
    }
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  return {
    highlightUIElement,
    clickUIElement,
    setUISelectValue,
    setUIInputValue,
  };
}
